import {
  AudioLines,
  Bell,
  ChartPie,
  Code,
  Hash,
  House,
  KeyRound,
  LifeBuoy,
  List,
  type LucideIcon,
  MessageCircle,
  MessageSquare,
  Mic,
  Phone,
  ShieldCheck,
  Workflow,
} from 'lucide-react'
import { SIP_TRUNKING_TAB_COPY } from 'v2/api-console/applications/sip-trunking-tabs'
import {
  PHONE_NUMBERS_TAB_COPY,
  type PhoneNumbersTabAccess,
  phoneNumbersTabState,
} from 'v2/api-console/numbers/phone-numbers-tabs'
import {
  COMPLIANCE_TAB_COPY,
  type ComplianceTabVisibility,
  isComplianceTabShown,
} from 'v2/pages/10dlc/compliance-tabs'
import { ALERTING_TAB_COPY, alertingTabLockSource } from 'v2/pages/alerting/alerting-tabs'
import {
  isTabAvailable,
  LOGS_TAB_COPY,
  type LogsTabLocks,
  resolveTabLockSource,
} from 'v2/pages/logs/logs-tab-locks'
import { WHATSAPP_TAB_COPY } from 'v2/pages/settings/whatsapp/whatsapp-tabs'
import type { SectionTab } from 'v2/shared/components/section-tabs'
import type { EnterpriseRequestSource } from 'v2/shared/utils/buddy-enterprise-request'

/** What the pages behind the sidebar decide about their own tabs, for this session. */
export interface NavContext {
  phoneNumbersAccess: PhoneNumbersTabAccess
  // An India org without an approved KYC: Phone Numbers shows only the KYC gate, no tabs.
  isIndiaKycRequired: boolean
  compliance: ComplianceTabVisibility
  hasWhatsAppTemplates: boolean
  isVerifyAvailable: boolean
  locks: LogsTabLocks
  hasBalanceAlerts: boolean
}

export enum Workspace {
  voiceAgents = 'voice-agents',
  telephony = 'telephony',
  voiceStudio = 'voice-studio',
}

/**
 * `gate` is the path of the route that decides access to this destination. An item is shown
 * only when the user may open that route, so the sidebar never links a page they can't open.
 */
export interface NavLinkConfig {
  key: string
  title: string
  path: string
  icon?: LucideIcon
  gate?: string
  // The page's own rule for this tab, so the link and the tab can't disagree.
  visible?: (ctx: NavContext) => boolean
  lockSource?: (ctx: NavContext) => EnterpriseRequestSource | undefined
}

export interface NavGroupConfig {
  key: string
  title: string
  icon: LucideIcon
  gate: string
  items: NavLinkConfig[]
}

// Analytics entries come from getReportSections, which already decides which report sections
// the session can see. 'agents' lists the AI Agents section inline; 'others' groups the rest.
export interface NavReportsConfig {
  key: string
  title: string
  icon: LucideIcon
  gate: string
  reports: 'agents' | 'others'
}

export type NavEntryConfig = NavLinkConfig | NavGroupConfig | NavReportsConfig

export interface NavSectionConfig {
  label: 'Build' | 'Connect' | 'Monitor'
  entries: NavEntryConfig[]
}

export interface WorkspaceConfig {
  id: Workspace
  title: string
  description: string
  icon: LucideIcon
  // The whole workspace is hidden when this route is; Voice Agents follows the Agents route,
  // which is what Ryuk's Enable/Disable Agent switch turns off.
  gate?: string
  // Pages that belong to this workspace alone. Any other page (Home, Phone Numbers, settings,
  // billing, conversations) is shared and keeps whichever workspace is current.
  ownedPrefixes: string[]
  sections: NavSectionConfig[]
}

export const isNavGroup = (entry: NavEntryConfig): entry is NavGroupConfig => 'items' in entry

export const isNavReports = (entry: NavEntryConfig): entry is NavReportsConfig => 'reports' in entry

const analyticsEntry = (reports: NavReportsConfig['reports']): NavReportsConfig => ({
  key: reports === 'agents' ? 'agent-analytics' : 'analytics',
  title: 'Analytics',
  icon: ChartPie,
  gate: '/analytics',
  reports,
})

export const HOME_LINK: NavLinkConfig = {
  key: 'home',
  title: 'Home',
  path: '/home',
  icon: House,
  gate: '/home',
}

export const SUPPORT_LINK: NavLinkConfig = {
  key: 'support',
  title: 'Support',
  path: '/support/portal',
  icon: LifeBuoy,
  gate: '/support/portal',
}

interface TabRules<T> {
  visible?: (tab: T, ctx: NavContext) => boolean
  lockSource?: (tab: T, ctx: NavContext) => EnterpriseRequestSource | undefined
}

// A tabbed page's links, one per tab at `<gate>/<tab>`, in the order and with the titles of the
// page's own copy map; the page's per-tab rules decide each link.
const tabGroup = <T extends string>(
  group: Omit<NavGroupConfig, 'items'>,
  copy: Record<T, SectionTab>,
  { visible, lockSource }: TabRules<T> = {},
): NavGroupConfig => ({
  ...group,
  items: (Object.keys(copy) as T[]).map((tab) => ({
    key: `${group.key}-${tab}`,
    title: copy[tab].title,
    path: `${group.gate}/${tab}`,
    visible: visible && ((ctx: NavContext) => visible(tab, ctx)),
    lockSource: lockSource && ((ctx: NavContext) => lockSource(tab, ctx)),
  })),
})

// The same pages in both workspaces: opening Phone Numbers never switches the workspace.
const phoneNumbersGroup = tabGroup(
  { key: 'phone-numbers', title: 'Phone Numbers', icon: Hash, gate: '/phone-numbers' },
  PHONE_NUMBERS_TAB_COPY,
  {
    visible: (tab, ctx) =>
      !ctx.isIndiaKycRequired && phoneNumbersTabState(tab, ctx.phoneNumbersAccess) === 'offered',
  },
)

export const WORKSPACES: WorkspaceConfig[] = [
  {
    id: Workspace.telephony,
    title: 'Telephony',
    description: 'Calls, messages & numbers',
    icon: Phone,
    ownedPrefixes: [
      '/applications',
      '/flow',
      '/sip-trunking',
      '/verify',
      '/whatsapp',
      '/compliance',
      '/logs',
      '/analytics',
      '/alerting',
    ],
    sections: [
      {
        label: 'Build',
        entries: [
          {
            key: 'applications',
            title: 'Applications',
            path: '/applications',
            icon: Code,
            gate: '/applications',
          },
          {
            key: 'flow',
            title: 'Flow',
            icon: Workflow,
            gate: '/flow',
            items: [
              { key: 'all-flows', title: 'All flows', path: '/flow' },
              { key: 'flow-runs', title: 'Flow runs', path: '/flow/runs', gate: '/flow/runs' },
            ],
          },
          tabGroup(
            { key: 'sip-trunking', title: 'SIP Trunking', icon: Phone, gate: '/sip-trunking' },
            SIP_TRUNKING_TAB_COPY,
          ),
          { key: 'verify', title: 'Verify', path: '/verify', icon: KeyRound, gate: '/verify' },
        ],
      },
      {
        label: 'Connect',
        entries: [
          phoneNumbersGroup,
          tabGroup(
            { key: 'whatsapp', title: 'WhatsApp', icon: MessageSquare, gate: '/whatsapp' },
            WHATSAPP_TAB_COPY,
            { visible: (tab, ctx) => tab !== 'templates' || ctx.hasWhatsAppTemplates },
          ),
          tabGroup(
            { key: 'compliance', title: 'Compliance', icon: ShieldCheck, gate: '/compliance' },
            COMPLIANCE_TAB_COPY,
            {
              visible: (tab, ctx) => isComplianceTabShown(tab, ctx.compliance),
            },
          ),
        ],
      },
      {
        label: 'Monitor',
        entries: [
          tabGroup({ key: 'logs', title: 'Logs', icon: List, gate: '/logs' }, LOGS_TAB_COPY, {
            visible: (tab, ctx) => isTabAvailable(tab, ctx.isVerifyAvailable),
            lockSource: (tab, ctx) => resolveTabLockSource(tab, ctx.locks),
          }),
          analyticsEntry('others'),
          tabGroup(
            { key: 'alerts', title: 'Alerts', icon: Bell, gate: '/alerting' },
            ALERTING_TAB_COPY,
            {
              visible: (tab, ctx) => tab !== 'balance-alerts' || ctx.hasBalanceAlerts,
              lockSource: (tab, ctx) => alertingTabLockSource(tab, ctx.locks.isEnterpriseLocked),
            },
          ),
        ],
      },
    ],
  },
  {
    id: Workspace.voiceAgents,
    title: 'Voice Agents',
    description: 'Build and run AI voice agents',
    icon: AudioLines,
    gate: '/agents',
    ownedPrefixes: [
      '/agents',
      '/agent-runs',
      '/agent',
      '/chat-widget',
      '/secrets',
      '/analytics/voice-agents',
    ],
    sections: [
      {
        label: 'Build',
        entries: [
          {
            key: 'agents',
            title: 'Agents',
            icon: Workflow,
            gate: '/agents',
            items: [
              { key: 'all-agents', title: 'All agents', path: '/agents' },
              { key: 'agent-runs', title: 'Agent runs', path: '/agent-runs', gate: '/agent-runs' },
            ],
          },
        ],
      },
      {
        label: 'Connect',
        entries: [
          phoneNumbersGroup,
          {
            key: 'chat-widget',
            title: 'Chat Widget',
            path: '/chat-widget',
            icon: MessageCircle,
            gate: '/chat-widget',
          },
        ],
      },
      { label: 'Monitor', entries: [analyticsEntry('agents')] },
    ],
  },
  {
    id: Workspace.voiceStudio,
    title: 'Voice Studio',
    description: 'Text-to-speech voices and generations',
    icon: Mic,
    gate: '/voice-studio',
    ownedPrefixes: ['/voice-studio'],
    sections: [
      {
        label: 'Build',
        entries: [
          {
            key: 'voice-studio',
            title: 'Voice Studio',
            path: '/voice-studio',
            icon: Mic,
            gate: '/voice-studio',
          },
        ],
      },
    ],
  },
]
